import {
    Button,
    Card,
    CardActions,
    CardContent,
    colors,
    Grid,
    Modal,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {useEffect, useState} from 'react';
import {T} from 'react-polyglot-hooks';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {useSnackbar} from 'notistack';
import anyLoading from '../../../utils/anyLoading';
import getPaymentSchedule from '../getPaymentSchedule.api';
import {
    PaymentSchedule,
    PaymentScheduleAction,
    PaymentScheduleAmountType,
    PaymentScheduleDate
} from '../../../graphql/payment_schedule.datatypes';
import {getHasError} from '../../../utils/formHelpers';
import validate from 'validate.js';
import PaymentScheduleDateEdit from './PaymentScheduleDateEdit';
import updatePaymentSchedule, {UpdatePaymentScheduleVars} from '../updatePaymentSchedule.api';
import {formatGraphQlDate} from '../../../utils/formatters';
import _ from 'underscore';
import ConfirmModal from '../../../components/ConfirmModal';
import cascadePaymentSchedule from './cascadePaymentSchedule.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: 800,
        maxHeight: '100%',
        overflowY: 'auto',
        maxWidth: '100%'
    },
    container: {
        marginTop: theme.spacing(3)
    },
    actions: {
        justifyContent: 'flex-end'
    },
    deleteButton: {
        color: theme.palette.white,
        backgroundColor: colors.red[600],
        '&:hover': {
            backgroundColor: colors.red[900]
        }
    },
    submitButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    scrollContent: {
        maxHeight: 500,
        overflow: 'scroll',
    },
}));

const getBlankPaymentScheduleDate = (schedule: PaymentSchedule) => {
    return {
        __typename: 'PaymentScheduleDate',
        id: -1 - schedule.dates.length,
        title: 'New Date',
        due_date: formatGraphQlDate(new Date()),//moment(new Date()).format('YYYY-MM-DD'),
        action_type: PaymentScheduleAction.PAYMENT,
        amount: 0,
        amount_type: PaymentScheduleAmountType.PERCENT,
        completed: false,
        calculatedAmount: 0,
    };
};

const PaymentScheduleDetailsModal = (props: {
    className?: string,
    onClose: () => void,
    onUpdate?: () => void,
    open: boolean,
    paymentScheduleId: number,
}) => {
    const {open, onClose, onUpdate, paymentScheduleId, ...rest} = props;
    const classes = useStyles();
    const [formState, setFormState] = useState<PaymentSchedule | undefined>();
    const [sendUpdate, updateData] = updatePaymentSchedule();
    const [sendCascade, cascadeData] = cascadePaymentSchedule();
    const paymentScheduleData = getPaymentSchedule(paymentScheduleId)
    const {enqueueSnackbar} = useSnackbar();
    const loadingData = anyLoading(paymentScheduleData, updateData, cascadeData);
    const schedule = paymentScheduleData.data && paymentScheduleData.data.paymentSchedule;

    useEffect(() => {
        if (paymentScheduleData.data && paymentScheduleData.data.paymentSchedule) {
            setFormState(paymentScheduleData.data.paymentSchedule);
        } else {
            setFormState(undefined);
        }
    }, [paymentScheduleData.data]);

    const [errors, setErrors] = useState({
        isValid: false,
        errors: {}
    });


    //2. Setup helper functions for form
    //const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        description: {
            presence: {allowEmpty: false, message: 'is required'},
        },
    };
    useEffect(() => {
        const newErrors = validate(formState, validateSchema);

        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);

    const checkCascade = () => {
        if (formState && (!!formState.tour || !!formState.group)) {
            setOpenCascade(true);
        } else {
            handleSave(false);
        }
    };

    const handleSave = (cascadeChange: boolean = false) => {
        if (formState) {
            const createDates = formState.dates
                .filter(date => date.id < 0)
                .map(date => ({
                    ...date,
                    __typename: undefined,
                    id: undefined,
                    calculatedAmount: undefined,
                }));

            const deleteDates = schedule!.dates
                .filter(date => formState.dates.find(checkDate => date.id === checkDate.id) === undefined)
                .map(date => date.id);

            const updateDates = formState.dates
                .filter(date => date.id > 0)
                .map(date => ({
                    ...date,
                    __typename: undefined,
                    schedule: undefined,
                    payment_schedule_id: undefined,
                    completed: undefined,
                    calculatedAmount: undefined,
                }));

            const sanitisedData: UpdatePaymentScheduleVars = {
                data: {
                    id: formState!.id,
                    description: formState!.description,
                    dates: {
                        create: createDates,
                        delete: deleteDates,
                        update: updateDates,
                    }
                }
            };

            sendUpdate({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Payment Schedule Saved.', {variant: 'success'});
                    if (cascadeChange) {
                        sendCascade({variables: {id: formState.id}})
                            .then(() => {
                                enqueueSnackbar('Cascade Payment Schedule Success.', {variant: 'success'});
                                onClose();
                                if (onUpdate) onUpdate();
                            })
                            .catch(() => {
                                enqueueSnackbar('Cascade Payment Schedule Failed!', {variant: 'error'});
                                onClose();
                                if (onUpdate) onUpdate();
                            });
                    } else {
                        onClose();
                        if (onUpdate) onUpdate();
                    }
                })
                .catch(() => {
                    enqueueSnackbar('Save Payment Schedule Failed!', {variant: 'error'});
                    onClose();
                });
        }
    };

    const [openCascade, setOpenCascade] = React.useState(false);

    if (!open) {
        return null;
    }

    return (
        <Modal onClose={onClose} open={open}>
            <Card {...rest} className={clsx(classes.root)}>
                <form>
                    <LoadingOverlay loading={loadingData.loading}>
                        <CardContent className={classes.scrollContent}>
                            <Typography gutterBottom variant="h3">Payment Schedule</Typography>

                            {formState && (
                                <Grid container spacing={3}>

                                    <Grid item xs={12}>
                                        <TextField
                                            {...hasError('description')}
                                            fullWidth
                                            label="Description"
                                            variant="outlined"
                                            name="description"
                                            multiline
                                            rows={3}
                                            value={formState.description}
                                            onChange={(event: any) => setFormState({
                                                ...formState,
                                                description: event.target.value
                                            })}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        {formState.dates.map((paymentDate) => (
                                            <PaymentScheduleDateEdit
                                                key={paymentDate.id}
                                                paymentScheduleDate={paymentDate}
                                                updatePaymentDate={(paymentScheduleDate: PaymentScheduleDate) => {
                                                    if (!_.isMatch(paymentScheduleDate, formState.dates.find(date => date.id === paymentScheduleDate.id))) {
                                                        const dates = formState.dates.map(date => date.id === paymentScheduleDate.id ? paymentScheduleDate : date);
                                                        setFormState({...formState, dates,})
                                                    }
                                                }}
                                                deletePaymentDate={(id: number) => setFormState({
                                                    ...formState,
                                                    dates: formState.dates.filter(date => date.id !== id)
                                                })}
                                            />)
                                        )}
                                    </Grid>

                                    <Grid item>
                                        <Button
                                            className={classes.submitButton}
                                            onClick={() => setFormState({
                                                ...formState,
                                                dates: [...formState.dates, getBlankPaymentScheduleDate(formState)]
                                            })}
                                            variant="contained"
                                        >Add Schedule Date</Button>
                                    </Grid>

                                </Grid>
                            )}

                        </CardContent>
                        <CardActions className={classes.actions}>
                            <Button onClick={() => onClose()} variant="contained">
                                <T phrase="Cancel"/>
                            </Button>
                            <Button className={classes.submitButton} onClick={checkCascade} variant="contained"
                                    disabled={!errors.isValid}>
                                <T phrase="Save"/>
                            </Button>
                        </CardActions>
                    </LoadingOverlay>
                </form>

                {openCascade && (
                    <ConfirmModal
                        onClose={() => setOpenCascade(false)}
                        cancel={'Dont Cascade Change'}
                        onCancelClick={() => handleSave(false)}
                        confirm={'Cascade Change'}
                        onConfirmClick={() => handleSave(true)}
                        open={true}
                        title={'Payment Schedule Updated!'}
                        message={'Would you like to cascade this change to Group and Billing Party payment schedules?'}
                    />
                )}
            </Card>
        </Modal>
    );
};

export default PaymentScheduleDetailsModal;
